import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  getMembershipOverlay,
  markMembershipPlayed,
  MembershipEvent,
  MembershipOverlay,
  pingMembershipOverlay,
} from '../../api/payment/membership.api';
import MemberBadge from '../../components/membership/member-badge';
import { retryQuietly } from '../page-live-effect-livestream/retry-quietly';
import { MemberAlertQueue } from './member-alert-queue';

const PING_MS = 30_000;
const RELOAD_WHEN_LOCKED_MS = 30_000;
const ALERT_MS = 6_000;
const ALERT_SOUND = '/sound/sound_1.mp3';
const MAX_RECENT_NAMES = 10;

const OUTLINE =
  '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

/** Bỏ tên trùng, giữ lần xuất hiện đầu (mới nhất), tối đa MAX_RECENT_NAMES tên. */
export const dedupeNames = (names: string[]) =>
  Array.from(new Set(names.filter(Boolean))).slice(0, MAX_RECENT_NAMES);

/** Chờ nối lại SignalR: 2, 4, 8, 16 giây rồi giữ 30 giây, thử mãi không bỏ. */
const signalRBackoffMs = (retryCount: number) =>
  Math.min(30_000, 2000 * 2 ** Math.min(retryCount, 10));

/** Overlay OBS hội viên kênh: nền trong suốt, báo hội viên mới, thanh mục tiêu, dải tên gần đây. Link có ?key=, không cần đăng nhập. */
const PageMemberLivestream: React.FC = () => {
  const { contentCreatorId } = useParams();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';
  const [overlay, setOverlay] = useState<MembershipOverlay | null>(null);
  const [current, setCurrent] = useState<MembershipEvent | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const queue = useRef(new MemberAlertQueue());
  const runningRef = useRef(false);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!contentCreatorId || !key) return false;
    try {
      const data = await getMembershipOverlay(contentCreatorId, key);
      loadedRef.current = true;
      setOverlay(data);
      setMemberCount(data.memberCount);
      data.queued.forEach((ev) => queue.current.push(ev));
      return true;
    } catch {
      return false; // khoá sai hoặc mạng lỗi: overlay đứng im, trong suốt
    }
  }, [contentCreatorId, key]);

  const pump = useCallback(() => {
    // Chưa tải được cấu hình (sai khoá hoặc sự kiện đến trước lần tải đầu): giữ lượt trong hàng đợi.
    if (!loadedRef.current || runningRef.current) return;
    const ev = queue.current.shift();
    if (!ev) return;
    runningRef.current = true;
    setMemberCount(ev.memberCount);
    setCurrent(ev);
  }, []);

  // Mỗi thông báo hiện 6 giây, kèm âm thanh; xong thì báo máy chủ rồi chạy lượt kế.
  useEffect(() => {
    if (!current || !contentCreatorId) return undefined;
    try {
      new Audio(ALERT_SOUND).play().catch(() => undefined);
    } catch {
      /* trình duyệt chặn tự phát âm thanh: bỏ qua */
    }
    const { joinId } = current;
    const id = window.setTimeout(() => {
      retryQuietly(() => markMembershipPlayed(contentCreatorId, key, joinId));
      runningRef.current = false;
      setCurrent(null);
    }, ALERT_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [current, contentCreatorId, key]);

  // Tải lần đầu; sai khoá thì thử lại mỗi 30 giây. Tải được thì báo "đang chạy" ngay, không đợi nhịp 30 giây đầu.
  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    let stopped = false;
    let timer = 0;
    loadedRef.current = false;
    // Đổi kênh/khoá: bỏ lượt đang chiếu dở và nhả cờ để hàng đợi không kẹt.
    runningRef.current = false;
    setCurrent(null);
    const attempt = () => {
      load().then((ok) => {
        if (stopped) return;
        if (ok) {
          pingMembershipOverlay(contentCreatorId, key).catch(() => undefined);
          pump();
        } else timer = window.setTimeout(attempt, RELOAD_WHEN_LOCKED_MS);
      });
    };
    attempt();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [contentCreatorId, key, load, pump]);

  useEffect(() => {
    const id = window.setInterval(pump, 500);
    return () => window.clearInterval(id);
  }, [pump]);

  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    const ping = () => {
      // Chưa tải được thì chưa báo "đang chạy" (sai khoá).
      if (!loadedRef.current) return;
      pingMembershipOverlay(contentCreatorId, key).catch(() => undefined);
    };
    const id = window.setInterval(ping, PING_MS);
    return () => window.clearInterval(id);
  }, [contentCreatorId, key]);

  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`,
        {
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: true,
        }
      )
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          signalRBackoffMs(ctx.previousRetryCount),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveMembership', (ev: MembershipEvent) => {
      if (!queue.current.push(ev)) return;
      // Thêm tên vào dải "Hội viên mới" ngay, không đợi lần tải lại.
      if (ev.viewerName)
        setOverlay((prev) =>
          prev
            ? {
                ...prev,
                recentNames: dedupeNames([ev.viewerName, ...prev.recentNames]),
              }
            : prev
        );
      pump();
    });
    let stopped = false;
    connection.onreconnected(() => {
      load().then(() => {
        if (!stopped) pump();
      });
    });
    let attempt = 0;
    let everStarted = false;
    let retryTimer = 0;
    const scheduleStart = () => {
      if (stopped) return;
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(start, signalRBackoffMs(attempt));
      attempt += 1;
    };
    function start() {
      if (stopped) return;
      connection
        .start()
        .then(() => {
          attempt = 0;
          if (everStarted)
            load().then(() => {
              if (!stopped) pump();
            });
          everStarted = true;
        })
        .catch(scheduleStart);
    }
    connection.onclose(scheduleStart);
    start();
    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      connection.stop();
    };
  }, [contentCreatorId, key, load, pump]);

  const root: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'transparent',
    pointerEvents: 'none',
    fontFamily: 'sans-serif',
  };
  if (!overlay) return <div style={root} />;

  const goal = overlay.goalTarget;
  const percent =
    goal > 0 ? Math.min(100, Math.round((memberCount / goal) * 100)) : 0;
  const names = dedupeNames(overlay.recentNames);

  return (
    <div style={root}>
      {current && (
        <div
          key={current.joinId}
          style={{
            position: 'absolute',
            top: '12%',
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            textShadow: OUTLINE,
            textAlign: 'center',
          }}
        >
          <MemberBadge size={64} />
          <div style={{ fontSize: 40, fontWeight: 800 }}>
            {current.viaCabPlus
              ? `${current.viewerName} vừa tham gia bằng lượt Cab+`
              : `${current.viewerName} vừa trở thành hội viên`}
          </div>
          <div style={{ fontSize: 24, fontWeight: 600 }}>
            {current.totalJoins > 1
              ? `Gia hạn lần ${current.totalJoins}`
              : `Hội viên thứ ${current.memberCount}`}
          </div>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 24,
          right: 24,
          bottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          color: '#fff',
          textShadow: OUTLINE,
        }}
      >
        {overlay.showRecentTicker && names.length > 0 && (
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {`Hội viên mới: ${names.join(' · ')}`}
          </div>
        )}
        {goal > 0 && (
          <>
            <div
              style={{
                height: 28,
                width: '100%',
                overflow: 'hidden',
                borderRadius: 9999,
                background: 'rgba(0,0,0,.4)',
                boxShadow: '0 0 0 2px rgba(0,0,0,.3)',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${percent}%`,
                  background: '#F59E0B',
                  transition: 'width .6s ease',
                }}
              />
            </div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {`${memberCount}/${goal} hội viên`}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PageMemberLivestream;
